import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Library,
  LayoutGrid,
  List,
  Sparkles,
  AlertCircle,
  Loader2,
  Search,
  TrendingUp,
  Clock,
  Tag,
} from 'lucide-react';
import { useFrameworksStore } from '@/stores/frameworks';
import type { CreativeFrameworkV2 as CreativeFramework } from '@/services/api';

const SOURCE_CATEGORY_LABELS: Record<string, string> = {
  classic_advertising: 'Classic Advertising',
  direct_response: 'Direct Response',
  short_form_video: 'Short-form Video',
  brand_narrative: 'Brand & Narrative',
  psychology_grounded: 'Psychology-Grounded',
};

const SOURCE_CATEGORY_COLORS: Record<string, string> = {
  classic_advertising: 'bg-amber-100 text-amber-800',
  direct_response: 'bg-rose-100 text-rose-800',
  short_form_video: 'bg-violet-100 text-violet-800',
  brand_narrative: 'bg-sky-100 text-sky-800',
  psychology_grounded: 'bg-emerald-100 text-emerald-800',
};

const PERFORMANCE_TIER_COLORS: Record<string, string> = {
  strong: 'bg-emerald-100 text-emerald-800',
  moderate: 'bg-amber-100 text-amber-800',
  weak: 'bg-slate-100 text-slate-600',
  unranked: 'bg-slate-50 text-slate-400',
};

export function FrameworkLibraryPage() {
  const {
    frameworks,
    loading,
    error,
    view,
    filters,
    setView,
    setFilter,
    fetchAll,
  } = useFrameworksStore();

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.source_category, filters.platform, filters.funnel_stage]);

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return frameworks;
    return frameworks.filter(
      (f) =>
        f.canonical_name.toLowerCase().includes(q) ||
        f.framework_id.toLowerCase().includes(q) ||
        (f.description || '').toLowerCase().includes(q) ||
        f.common_aliases.some((a) => a.toLowerCase().includes(q)),
    );
  }, [frameworks, filters.search]);

  const groupedByCategory = useMemo(() => {
    const groups: Record<string, CreativeFramework[]> = {};
    for (const f of filtered) {
      const k = f.source_category;
      if (!groups[k]) groups[k] = [];
      groups[k].push(f);
    }
    return groups;
  }, [filtered]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <Library size={22} className="text-brand-coral" />
            Creative Frameworks Library
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Curated persuasion structures driving brief, angle, and script generation.
            {frameworks.length > 0 && ` ${frameworks.length} frameworks loaded.`}
          </p>
        </div>
        <ViewToggle view={view} onChange={setView} />
      </div>

      <FilterBar filters={filters} setFilter={setFilter} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {loading && frameworks.length === 0 ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" />
          Loading frameworks…
        </div>
      ) : view === 'list' ? (
        <ListView frameworks={filtered} />
      ) : view === 'research_feed' ? (
        <ResearchFeedView frameworks={filtered} />
      ) : (
        <GridView grouped={groupedByCategory} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ViewToggle({
  view,
  onChange,
}: {
  view: 'grid' | 'list' | 'research_feed';
  onChange: (v: 'grid' | 'list' | 'research_feed') => void;
}) {
  const opts: Array<{ v: typeof view; icon: React.ReactNode; label: string }> = [
    { v: 'grid', icon: <LayoutGrid size={14} />, label: 'Grid' },
    { v: 'list', icon: <List size={14} />, label: 'List' },
    { v: 'research_feed', icon: <Sparkles size={14} />, label: 'Research Feed' },
  ];
  return (
    <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
      {opts.map(({ v, icon, label }) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={clsx(
            'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
            view === v
              ? 'bg-white text-navy-900 shadow-sm'
              : 'text-slate-500 hover:text-navy-900',
          )}
        >
          {icon}
          {label}
        </button>
      ))}
    </div>
  );
}

function FilterBar({
  filters,
  setFilter,
}: {
  filters: { search: string; source_category?: string; platform?: string; funnel_stage?: string };
  setFilter: <K extends keyof typeof filters>(key: K, value: typeof filters[K]) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-slate-200 px-4 py-3">
      <div className="flex items-center gap-2 flex-1 min-w-[200px]">
        <Search size={14} className="text-slate-400" />
        <input
          type="text"
          placeholder="Search frameworks…"
          value={filters.search}
          onChange={(e) => setFilter('search', e.target.value)}
          className="flex-1 text-sm border-0 outline-none placeholder:text-slate-400 bg-transparent"
        />
      </div>
      <FilterSelect
        label="Category"
        value={filters.source_category || ''}
        onChange={(v) => setFilter('source_category', v || undefined)}
        options={Object.entries(SOURCE_CATEGORY_LABELS).map(([k, label]) => ({
          value: k,
          label,
        }))}
      />
      <FilterSelect
        label="Platform"
        value={filters.platform || ''}
        onChange={(v) => setFilter('platform', v || undefined)}
        options={[
          { value: 'meta', label: 'Meta' },
          { value: 'tiktok', label: 'TikTok' },
          { value: 'youtube_shorts', label: 'YouTube Shorts' },
          { value: 'email', label: 'Email' },
          { value: 'landing_page', label: 'Landing Page' },
        ]}
      />
      <FilterSelect
        label="Funnel"
        value={filters.funnel_stage || ''}
        onChange={(v) => setFilter('funnel_stage', v || undefined)}
        options={[
          { value: 'awareness', label: 'Awareness' },
          { value: 'consideration', label: 'Consideration' },
          { value: 'conversion', label: 'Conversion' },
        ]}
      />
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs font-medium border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-navy-900"
    >
      <option value="">{label}: All</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {label}: {o.label}
        </option>
      ))}
    </select>
  );
}

function GridView({ grouped }: { grouped: Record<string, CreativeFramework[]> }) {
  const orderedKeys = [
    'classic_advertising',
    'direct_response',
    'short_form_video',
    'brand_narrative',
    'psychology_grounded',
  ].filter((k) => grouped[k]?.length);
  return (
    <div className="space-y-8">
      {orderedKeys.map((cat) => (
        <section key={cat}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {SOURCE_CATEGORY_LABELS[cat]}
              <span className="ml-2 text-slate-400 font-normal lowercase normal-case">
                ({grouped[cat].length})
              </span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {grouped[cat].map((f) => (
              <FrameworkCard key={f.framework_id} framework={f} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function FrameworkCard({ framework }: { framework: CreativeFramework }) {
  return (
    <Link
      to={`/frameworks/${framework.framework_id}`}
      className="block bg-white rounded-xl border border-slate-200 hover:border-brand-coral hover:shadow-md transition-all p-4"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-navy-900 truncate">
            {framework.canonical_name}
          </h3>
          <code className="text-[10px] text-slate-400">{framework.framework_id}</code>
        </div>
        <span
          className={clsx(
            'px-2 py-0.5 text-[10px] font-medium rounded-full whitespace-nowrap',
            PERFORMANCE_TIER_COLORS[framework.overall_performance_tier] ||
              PERFORMANCE_TIER_COLORS.unranked,
          )}
        >
          {framework.overall_performance_tier}
        </span>
      </div>
      {framework.description && (
        <p className="text-xs text-slate-600 line-clamp-2 mb-3">
          {framework.description}
        </p>
      )}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span
          className={clsx(
            'px-2 py-0.5 text-[10px] font-medium rounded',
            SOURCE_CATEGORY_COLORS[framework.source_category],
          )}
        >
          {SOURCE_CATEGORY_LABELS[framework.source_category] || framework.source_category}
        </span>
        <span className="text-[10px] text-slate-500 flex items-center gap-1">
          <Tag size={10} /> {framework.segments.length} seg
        </span>
        <span className="text-[10px] text-slate-500 flex items-center gap-1">
          <TrendingUp size={10} /> {framework.client_ad_count + framework.extracted_ad_count} ads
        </span>
        {framework.knowledge_last_updated && (
          <span className="text-[10px] text-slate-500 flex items-center gap-1">
            <Clock size={10} />
            {new Date(framework.knowledge_last_updated).toLocaleDateString()}
          </span>
        )}
      </div>
    </Link>
  );
}

function ListView({ frameworks }: { frameworks: CreativeFramework[] }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase">
          <tr>
            <th className="text-left px-4 py-2 font-semibold">Framework</th>
            <th className="text-left px-4 py-2 font-semibold">Category</th>
            <th className="text-left px-4 py-2 font-semibold">Originator</th>
            <th className="text-right px-4 py-2 font-semibold">Segments</th>
            <th className="text-right px-4 py-2 font-semibold">Ads</th>
            <th className="text-right px-4 py-2 font-semibold">Findings</th>
            <th className="text-left px-4 py-2 font-semibold">Last Researched</th>
          </tr>
        </thead>
        <tbody>
          {frameworks.map((f) => (
            <tr key={f.framework_id} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="px-4 py-2">
                <Link
                  to={`/frameworks/${f.framework_id}`}
                  className="font-medium text-navy-900 hover:text-brand-coral"
                >
                  {f.canonical_name}
                </Link>
                <div className="text-[10px] text-slate-400">{f.framework_id}</div>
              </td>
              <td className="px-4 py-2">
                <span
                  className={clsx(
                    'px-2 py-0.5 text-[10px] font-medium rounded',
                    SOURCE_CATEGORY_COLORS[f.source_category],
                  )}
                >
                  {SOURCE_CATEGORY_LABELS[f.source_category] || f.source_category}
                </span>
              </td>
              <td className="px-4 py-2 text-xs text-slate-600">
                {f.originator || '—'}
              </td>
              <td className="px-4 py-2 text-right text-xs text-slate-600">
                {f.segments.length}
              </td>
              <td className="px-4 py-2 text-right text-xs text-slate-600">
                {f.client_ad_count + f.extracted_ad_count}
              </td>
              <td className="px-4 py-2 text-right text-xs text-slate-600">
                {f.key_findings.length}
              </td>
              <td className="px-4 py-2 text-xs text-slate-500">
                {f.knowledge_last_updated
                  ? new Date(f.knowledge_last_updated).toLocaleDateString()
                  : 'Never'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {frameworks.length === 0 && (
        <div className="text-center text-sm text-slate-400 py-8">No frameworks match.</div>
      )}
    </div>
  );
}

function ResearchFeedView({ frameworks }: { frameworks: CreativeFramework[] }) {
  // Sort by knowledge_last_updated desc; show recent activity
  const sorted = [...frameworks].sort((a, b) => {
    const ad = a.knowledge_last_updated ? new Date(a.knowledge_last_updated).valueOf() : 0;
    const bd = b.knowledge_last_updated ? new Date(b.knowledge_last_updated).valueOf() : 0;
    return bd - ad;
  });
  return (
    <div className="space-y-3">
      {sorted.map((f) => (
        <Link
          key={f.framework_id}
          to={`/frameworks/${f.framework_id}`}
          className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 hover:border-brand-coral p-4"
        >
          <div className="w-1 self-stretch bg-brand-coral rounded" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-navy-900">{f.canonical_name}</h3>
              <span className="text-[10px] text-slate-400">v{f.version}</span>
            </div>
            <div className="text-xs text-slate-500">
              {f.research_report_ids.length} research report
              {f.research_report_ids.length === 1 ? '' : 's'} •{' '}
              {f.refinement_history.length} refinement
              {f.refinement_history.length === 1 ? '' : 's'} •{' '}
              {f.knowledge_last_updated
                ? `Updated ${new Date(f.knowledge_last_updated).toLocaleDateString()}`
                : 'Never researched'}
            </div>
            {f.key_findings.length > 0 && (
              <p className="text-xs text-slate-700 mt-2 line-clamp-2">
                Latest finding: <em>{f.key_findings[0]}</em>
              </p>
            )}
          </div>
        </Link>
      ))}
      {sorted.length === 0 && (
        <div className="text-center text-sm text-slate-400 py-8">No frameworks.</div>
      )}
    </div>
  );
}
